// frontend/src/components/areas/AreasEmptyMobile.tsx
// NEW: Mobile-first empty state component

import React from 'react';
import { 
  MapPin, 
  Plus, 
  Search, 
  Users, 
  Filter,
  AlertCircle,
  RefreshCw
} from 'lucide-react';

interface AreasEmptyMobileProps {
  hasFilters: boolean;
  onCreateArea: () => void;
  onClearFilters: () => void;
  isAdmin?: boolean;
}

const AreasEmptyMobile: React.FC<AreasEmptyMobileProps> = ({ 
  hasFilters, 
  onCreateArea, 
  onClearFilters,
  isAdmin = false 
}) => {
  
  if (hasFilters) {
    // Empty state when filters are applied
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
        <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
          isAdmin ? 'bg-blue-100' : 'bg-gray-100'
        }`}>
          <Search className={`h-8 w-8 ${isAdmin ? 'text-blue-600' : 'text-gray-400'}`} />
        </div>
        
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No areas found
        </h3>
        
        <p className="text-gray-600 mb-6">
          {isAdmin 
            ? "No service areas match your current filters. Try adjusting your search criteria."
            : "No areas found matching your filters. Try adjusting your search criteria."
          }
        </p>

        <div className="space-y-3">
          <button
            onClick={onClearFilters}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter className="h-4 w-4" />
            Clear Filters
          </button>
          
          {!isAdmin && (
            <button
              onClick={onCreateArea}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add New Area
            </button>
          )}
        </div>
      </div>
    );
  }

  // Empty state when no areas exist at all
  if (isAdmin) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <div className="w-20 h-20 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-6">
          <Users className="h-10 w-10 text-blue-600" />
        </div>
        
        <h3 className="text-xl font-semibold text-gray-900 mb-3">
          No Service Areas Found
        </h3>
        
        <p className="text-gray-600 mb-6 leading-relaxed">
          There are no service areas in the system yet. Service areas are created by individual 
          providers to define their coverage zones.
        </p>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="h-5 w-5 text-blue-600" />
            <span className="font-medium text-blue-900">Admin Information</span>
          </div>
          <p className="text-sm text-blue-700 text-left">
            • Providers create areas during onboarding<br/>
            • Areas define where providers offer services<br/>
            • You can view and monitor all areas system-wide<br/>
            • Contact providers directly for area modifications
          </p>
        </div>

        <button
          onClick={() => window.location.reload()}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mx-auto"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh Data
        </button>
      </div>
    );
  }

  // Provider empty state
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
      <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
        <MapPin className="h-10 w-10 text-green-600" />
      </div>
      
      <h3 className="text-xl font-semibold text-gray-900 mb-3">
        Add Your First Service Area
      </h3>
      
      <p className="text-gray-600 mb-6 leading-relaxed">
        Service areas help customers find you based on their location. Start by adding the areas 
        where you provide services.
      </p>

      {/* Benefits */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-left">
        <h4 className="font-medium text-green-900 mb-3">Benefits of adding service areas:</h4>
        <ul className="text-sm text-green-700 space-y-2">
          <li className="flex items-start gap-2">
            <div className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
            <span>Customers can easily find you in their area</span>
          </li>
          <li className="flex items-start gap-2">
            <div className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
            <span>Increase visibility in search results</span>
          </li>
          <li className="flex items-start gap-2">
            <div className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
            <span>Better manage your service coverage</span>
          </li>
          <li className="flex items-start gap-2">
            <div className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
            <span>Get more relevant booking requests</span>
          </li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        <button
          onClick={onCreateArea}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          <Plus className="h-5 w-5" />
          Add Service Area
        </button>
        
        <button
          onClick={() => window.location.reload()}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Quick Tips */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg text-left">
        <h4 className="font-medium text-gray-900 mb-2">Quick Tips:</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Start with your primary service area</li>
          <li>• Add multiple areas to expand your reach</li>
          <li>• You can activate/deactivate areas anytime</li>
          <li>• Use specific pincodes for better targeting</li>
        </ul>
      </div>
    </div>
  );
};

export default AreasEmptyMobile;